import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import NavigationComponent from "../components/NavigationComponent";
import CycleCalendar from "./components/CycleCalendar";
import {
  getPredictedCycles,
  getAvailableMonths,
  formatLogsForCalendar,
  getNormalizedLogs,
  hasPeriodSettings,
} from "./menstrual-cycle.helper";

const MenstrualCycle = ({ locals, selectedMonth }) => {
  const { t } = useTranslation("athlete-portal");
  const { query, pathname, femaleCycleLogs, femaleCycleSettings } = locals;
  const startingCalendarDay = locals.activeUser.startingCalendarDay || 0;
  const theme = locals.activeUser.webAppTheme || "dark";

  // Always include settings-derived log (first registered period) when present
  const normalizedLogs = useMemo(() => {
    return getNormalizedLogs(femaleCycleLogs, femaleCycleSettings);
  }, [femaleCycleLogs, femaleCycleSettings]);

  // When user has settings and no logs (with first registered period), show calendar
  const hasOnlySettings =
    (!femaleCycleLogs || femaleCycleLogs.length === 0) &&
    hasPeriodSettings(femaleCycleSettings) &&
    femaleCycleSettings?.firstRegisteredPeriod;

  // Get available months (includes month of settings log when no user logs)
  const availableMonths = useMemo(() => {
    return getAvailableMonths(normalizedLogs);
  }, [normalizedLogs]);

  // Calculate predictions for the next 5 months (uses normalized logs so settings log counts)
  const predictions = useMemo(() => {
    return getPredictedCycles(
      normalizedLogs,
      femaleCycleSettings,
      selectedMonth,
      5
    );
  }, [normalizedLogs, femaleCycleSettings, selectedMonth]);

  // Format calendar data (normalized logs so settings log is always shown)
  const defaultPeriodDuration = femaleCycleSettings?.averagePeriodLength ?? 5;
  const calendarData = useMemo(() => {
    return formatLogsForCalendar(
      normalizedLogs,
      predictions,
      selectedMonth,
      defaultPeriodDuration
    );
  }, [normalizedLogs, predictions, selectedMonth, defaultPeriodDuration]);

  const showCalendar =
    (femaleCycleLogs && femaleCycleLogs.length > 0) || hasOnlySettings;

  return (
    <main role="reports-wrapper">
      <NavigationComponent
        query={query}
        textKey="nav.calendarDayView"
        pathName={pathname}
        classNames="mobile-hidden"
      />
      <article id="menstrual-cycle" role="report-content">
        <header>
          <hgroup>
            <h2>{t("menstrualCycle.title")}</h2>
          </hgroup>
        </header>

        <div className="menstrual-cycle-content">
          {showCalendar ? (
            <CycleCalendar
              selectedMonth={selectedMonth}
              calendarData={calendarData}
              availableMonths={availableMonths}
              startingCalendarDay={startingCalendarDay}
              theme={theme}
            />
          ) : (
            <div className="no-logs-message">
              <h4>{t("menstrualCycle.noData")}</h4>
              <p className="p2 mt-2">
                {t("menstrualCycle.clientNoData")}
              </p>
            </div>
          )}
        </div>
      </article>
    </main>
  );
};

export default MenstrualCycle;
